import React, { useState, useEffect, useMemo, useRef, useId, lazy, Suspense } from 'react';
import type { Comparison } from '../../types/schema';
import type { Product } from '../../types/product';
import { formatPrice, parseRating, parseReviewCount } from '../../types/product';
import { ProductImage, Badge, Card, Button, StarRating } from '../base';
import { withSharedStyles, sharedClassNames } from './sharedStyles';
import { useProductCacheOptional } from '../../context/ProductCache';
import type { OnAddToCartCallback } from '../../types/cart';
import { logger } from '../../../utils/logger';
import { registerSelection, unregisterSelection } from '../../../hooks/useSelectionRegistry';

const ProductView = lazy(() => import('./ProductView'));

export interface ComparisonViewProps {
  comparison: Comparison;
  apiBaseUrl?: string;
  messageId?: string;
  chatContext?: string;
  onAddToCart?: OnAddToCartCallback;
  className?: string;
  style?: React.CSSProperties;
}

const COMPONENT_ID = 'comparison-view';
const COMPACT_BREAKPOINT = 550;
const MAX_VISIBLE_DIFFS = 5;

// ── Component CSS (injected via <style>) ──
const componentStyles = `
  .${COMPONENT_ID}-image-stage {
    background: linear-gradient(to bottom, #f0ebe0 0%, #d8d0be 100%) !important;
    padding: 1.25em !important;
    display: flex !important;
    gap: 0.75em !important;
    align-items: stretch !important;
    height: 280px !important;
  }

  .${COMPONENT_ID}-image-stage > * {
    flex: 1 !important;
    min-width: 0 !important;
    background-color: transparent !important;
  }

  .${COMPONENT_ID}-scroll {
    display: flex !important;
    gap: 0.75em !important;
    overflow-x: auto !important;
    scroll-snap-type: x mandatory !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    padding: 1.25em !important;
    height: 280px !important;
    background: linear-gradient(to bottom, #f0ebe0 0%, #d8d0be 100%) !important;
  }

  .${COMPONENT_ID}-scroll::-webkit-scrollbar {
    display: none !important;
  }

  .${COMPONENT_ID}-scroll-item {
    flex: 0 0 45% !important;
    scroll-snap-align: start !important;
    min-width: 0 !important;
  }

  .${COMPONENT_ID}-scroll-item > div {
    background-color: transparent !important;
  }

  .${COMPONENT_ID}-info-grid > * {
    padding-top: 0.125em !important;
    padding-bottom: 0.125em !important;
  }

  .${COMPONENT_ID}-diff-row:nth-child(odd) {
    background: var(--color-cream, #faf9f7) !important;
    border-radius: 6px !important;
  }

  .${COMPONENT_ID}-diff-row {
    padding: 0.5em 0 !important;
  }
`;

export function ComparisonView({
  comparison,
  apiBaseUrl = '/api',
  messageId,
  chatContext,
  onAddToCart,
  className,
  style,
}: ComparisonViewProps) {
  const componentId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [productsData, setProductsData] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showAllDiffs, setShowAllDiffs] = useState(false);
  const [selectedProductSku, setSelectedProductSku] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [scrollIndex, setScrollIndex] = useState(0);

  const productCache = useProductCacheOptional();
  const productCount = comparison.products.length;
  const isCompact = containerWidth > 0 && containerWidth < COMPACT_BREAKPOINT;
  const needs3Scroll = productCount === 3 && isCompact;

  const productsKey = useMemo(
    () => comparison.products.map(p => p.productSku).join(','),
    [comparison.products]
  );

  // Measure container
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const skus = comparison.products.map(p => p.productSku);
      let newData: Record<string, Product>;

      if (productCache) {
        newData = await productCache.getProducts(skus, apiBaseUrl);
      } else {
        newData = {};
        await Promise.all(
          skus.map(async (sku) => {
            try {
              const response = await fetch(`${apiBaseUrl}/api/product/${sku}`, {
                headers: { 'ngrok-skip-browser-warning': 'true' },
              });
              if (response.ok) {
                newData[sku] = await response.json();
              }
            } catch (error) {
              logger.error(`[ComparisonView] Error fetching product ${sku}`, {
                sku,
                errorMessage: error instanceof Error ? error.message : String(error),
              });
            }
          })
        );
      }

      // Preload first product image
      const firstSku = comparison.products[0]?.productSku;
      const firstProduct = firstSku ? newData[firstSku] : null;
      const imageUrl = firstProduct?.transparent_image_url || firstProduct?.image_url;
      if (imageUrl) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = imageUrl;
        });
      }

      setProductsData(newData);
      setLoading(false);
    };

    if (comparison.products.length > 0) {
      fetchProducts();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsKey, apiBaseUrl]);

  // Stable key for productsData
  const productsDataKey = useMemo(
    () => Object.keys(productsData).sort().join(','),
    [productsData]
  );

  // Selection registry
  useEffect(() => {
    if (!messageId || loading) return;
    const firstSku = comparison.products[0]?.productSku;
    const firstProduct = firstSku ? productsData[firstSku] : null;
    if (!firstSku) return;
    registerSelection(componentId, {
      type: 'product-list',
      messageId,
      selectedItem: {
        productSku: firstSku,
        productName: firstProduct?.name || firstSku,
        productColor: firstProduct?.color || undefined,
      },
    });
    return () => unregisterSelection(componentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, messageId, loading, productsKey, productsDataKey]);

  const visibleDiffs = showAllDiffs
    ? comparison.differences
    : comparison.differences.slice(0, MAX_VISIBLE_DIFFS);
  const hasMoreDiffs = comparison.differences.length > MAX_VISIBLE_DIFFS;
  const sharedItems = comparison.shared || [];

  const handleProductClick = (sku: string) => {
    setSelectedProductSku(sku);
    setShowModal(true);
  };

  // Scroll-snap handlers
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleDotClick = (index: number) => {
    if (!scrollContainerRef.current || productCount === 0) return;
    const cardWidth = scrollContainerRef.current.scrollWidth / productCount;
    scrollContainerRef.current.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
  };
  const handleScroll = () => {
    if (!scrollContainerRef.current || productCount === 0) return;
    const cardWidth = scrollContainerRef.current.scrollWidth / productCount;
    const idx = Math.round(scrollContainerRef.current.scrollLeft / cardWidth);
    setScrollIndex(idx);
  };

  // Helper: extract category from product data
  const getCategory = (product: Product | undefined): string | null => {
    if (!product?.category_path) return null;
    const segments = product.category_path.split(' > ');
    return segments[segments.length - 1] || null;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div ref={containerRef} className={`${sharedClassNames.wrapper} ${COMPONENT_ID}-wrapper ${className || ''}`} style={style}>
        <style>{withSharedStyles(componentStyles)}</style>
        <Card padding="none" shadow="lg">
          <div className={`${COMPONENT_ID}-image-stage`}>
            {comparison.products.map((_, i) => (
              <div key={i} style={inlineStyles.skeletonImage} />
            ))}
          </div>
          <div style={inlineStyles.content}>
            <div style={{ ...inlineStyles.infoGrid, gridTemplateColumns: `repeat(${comparison.products.length}, 1fr)` }}>
              {comparison.products.map((_, i) => (
                <React.Fragment key={i}>
                  <div style={{ ...inlineStyles.skeletonLine, width: '50%' }} />
                </React.Fragment>
              ))}
              {comparison.products.map((_, i) => (
                <div key={`n-${i}`} style={{ ...inlineStyles.skeletonLine, width: '80%' }} />
              ))}
              {comparison.products.map((_, i) => (
                <div key={`p-${i}`} style={{ ...inlineStyles.skeletonLine, width: '30%' }} />
              ))}
            </div>
            <div style={{ ...inlineStyles.skeletonLine, width: '100%', height: '3em' }} />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`${sharedClassNames.wrapper} ${COMPONENT_ID}-wrapper ${className || ''}`} style={style}>
      <style>{withSharedStyles(componentStyles)}</style>
      <Card padding="none" shadow="lg">

        {/* ── Image Stage (tan gradient) ── */}
        {needs3Scroll ? (
          // 3-product mobile: scroll-snap images
          <>
            <div
              ref={scrollContainerRef}
              className={`${COMPONENT_ID}-scroll`}
              onScroll={handleScroll}
            >
              {comparison.products.map((cp, i) => {
                const product = productsData[cp.productSku];
                const imageUrl = product?.transparent_image_url || product?.image_url || null;
                return (
                  <div key={cp.productSku} className={`${COMPONENT_ID}-scroll-item`}>
                    <ProductImage
                      src={imageUrl}
                      alt={product?.name || cp.productSku}
                      fillContainer
                      objectFit="contain"
                      borderRadius="md"
                    />
                  </div>
                );
              })}
            </div>
            <div style={inlineStyles.dotsRow}>
              {comparison.products.map((_, i) => (
                <button
                  key={i}
                  style={{
                    ...inlineStyles.dot,
                    ...(i === scrollIndex ? inlineStyles.dotActive : {}),
                  }}
                  onClick={() => handleDotClick(i)}
                  aria-label={`Product ${i + 1}`}
                />
              ))}
            </div>
          </>
        ) : (
          // 2-product or desktop 3-product: side-by-side images
          <div className={`${COMPONENT_ID}-image-stage`}>
            {comparison.products.map((cp) => {
              const product = productsData[cp.productSku];
              const imageUrl = product?.transparent_image_url || product?.image_url || null;
              return (
                <ProductImage
                  key={cp.productSku}
                  src={imageUrl}
                  alt={product?.name || cp.productSku}
                  fillContainer
                  objectFit="contain"
                  borderRadius="md"
                />
              );
            })}
          </div>
        )}

        {/* ── Content (white bg) ── */}
        <div style={inlineStyles.content}>

          {/* Product Info Grid — grid rows auto-align across columns */}
          <div className={`${COMPONENT_ID}-info-grid`} style={{ ...inlineStyles.infoGrid, gridTemplateColumns: `repeat(${productCount}, 1fr)`, position: 'relative' as const }}>
            {/* Category row */}
            {comparison.products.map((cp, ci) => {
              const category = getCategory(productsData[cp.productSku]);
              return <div key={`cat-${cp.productSku}`} style={{ ...inlineStyles.category, ...(ci > 0 ? inlineStyles.colPadding : {}) }}>{category || '\u00A0'}</div>;
            })}
            {/* Name row */}
            {comparison.products.map((cp, ci) => {
              const product = productsData[cp.productSku];
              return <div key={`name-${cp.productSku}`} style={{ ...inlineStyles.productName, ...(ci > 0 ? inlineStyles.colPadding : {}) }}>{product?.name || cp.productSku}</div>;
            })}
            {/* Price row */}
            {comparison.products.map((cp, ci) => {
              const product = productsData[cp.productSku];
              const price = product?.price ?? null;
              const salePrice: number | null = product?.sale_price ?? null;
              return (
                <div key={`price-${cp.productSku}`} style={{ ...inlineStyles.priceRow, ...(ci > 0 ? inlineStyles.colPadding : {}) }}>
                  {salePrice && salePrice < (price ?? Infinity) ? (
                    <>
                      <span style={inlineStyles.price}>{formatPrice(salePrice)}</span>
                      <span style={inlineStyles.priceStrike}>{formatPrice(price)}</span>
                    </>
                  ) : (
                    <span style={inlineStyles.price}>{formatPrice(price)}</span>
                  )}
                </div>
              );
            })}
            {/* Rating row — only if at least one product has a rating */}
            {comparison.products.some(cp =>
              parseRating(productsData[cp.productSku]?.review_rating) !== null
            ) && comparison.products.map((cp, ci) => {
              const product = productsData[cp.productSku];
              const rating = parseRating(product?.review_rating);
              const count = parseReviewCount(product?.review_count);
              return (
                <div key={`rating-${cp.productSku}`} style={{
                  padding: '0.125em 0',
                  ...(ci > 0 ? inlineStyles.colPadding : {}),
                }}>
                  {rating !== null
                    ? <StarRating rating={rating} reviewCount={count} variant="compact" />
                    : '\u00A0'}
                </div>
              );
            })}
            {/* Badge row */}
            {comparison.products.map((cp, ci) => (
              <div key={`badge-${cp.productSku}`} style={{ ...inlineStyles.badgeRow, ...(ci > 0 ? inlineStyles.colPadding : {}) }}>
                {cp.label ? <Badge variant="accent" size="sm">{cp.label}</Badge> : '\u00A0'}
              </div>
            ))}
            {/* Subtitle row */}
            {comparison.products.map((cp, ci) => (
              <div key={`sub-${cp.productSku}`} style={{ ...inlineStyles.subtitle, ...(ci > 0 ? inlineStyles.colPadding : {}) }}>
                {cp.subtitle || '\u00A0'}
              </div>
            ))}
            {/* Continuous vertical divider line(s) at exact center */}
            {Array.from({ length: productCount - 1 }, (_, idx) => {
              const i = idx + 1;
              return (
                <div key={`info-divider-${i}`} style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `calc(${(i / productCount) * 100}%)`,
                  width: '1px',
                  background: 'var(--color-stone, #e8e6e3)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }} />
              );
            })}
          </div>

          {/* Recommendation (verdict first) */}
          {comparison.recommendation && (
            <div style={inlineStyles.recommendation}>
              <span style={inlineStyles.recommendationText}>{comparison.recommendation}</span>
            </div>
          )}

          {/* Shared Traits (after recommendation — shared context before differences) */}
          {sharedItems.length > 0 && (
            <div style={inlineStyles.sharedSection}>
              <div style={inlineStyles.sectionLabel}>In Common</div>
              <div style={inlineStyles.sharedRow}>
                {sharedItems.map((item, i) => (
                  <span key={i} style={inlineStyles.sharedChip}>{item}</span>
                ))}
              </div>
            </div>
          )}

          {/* Key Differences (label outside diffsSection for correct nth-child shading) */}
          {visibleDiffs.length > 0 && (
            <div>
              <div style={inlineStyles.sectionLabel}>Key Differences</div>
              <div style={{ ...inlineStyles.diffsSection, position: 'relative' as const }}>
                {visibleDiffs.map((diff, i) => (
                  <div key={diff.attribute} className={`${COMPONENT_ID}-diff-row`}>
                    <div style={inlineStyles.diffAttrLabel}>{diff.attribute}</div>
                    <div style={inlineStyles.diffValuesRow}>
                      {diff.values.map((val, vi) => (
                        <div key={vi} style={{
                          ...inlineStyles.diffValue,
                          ...(vi > 0 ? inlineStyles.colPadding : {}),
                        }}>
                          {val}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {hasMoreDiffs && !showAllDiffs && (
                  <button style={inlineStyles.expandBtn} onClick={() => setShowAllDiffs(true)}>
                    Show all {comparison.differences.length} differences
                  </button>
                )}
                {/* Continuous vertical divider line(s) — after diff rows so nth-child shading is unaffected */}
                {Array.from({ length: productCount - 1 }, (_, idx) => {
                  const i = idx + 1;
                  return (
                    <div key={`divider-${i}`} style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `calc(${(i / productCount) * 100}%)`,
                      width: '1px',
                      background: 'var(--color-stone, #e8e6e3)',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }} />
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div style={inlineStyles.ctaRow}>
            {comparison.products.map((cp) => (
              <div key={cp.productSku} style={{ flex: 1 }}>
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={() => handleProductClick(cp.productSku)}
                >
                  Add to Cart
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Product Detail Modal */}
      {showModal && selectedProductSku && (
        <Suspense fallback={null}>
          <div className={sharedClassNames.modal}>
            <div
              className={sharedClassNames.modalOverlay}
              onClick={() => setShowModal(false)}
            />
            <div className={sharedClassNames.modalContent}>
              <ProductView
                product={{
                  productSku: selectedProductSku,
                  annotations: {},
                }}
                apiBaseUrl={apiBaseUrl}
                showFullDetails={true}
                chatContext={chatContext}
                onClose={() => setShowModal(false)}
                onAddToCart={(items, context) => {
                  onAddToCart?.(items, context);
                  setShowModal(false);
                }}
              />
            </div>
          </div>
        </Suspense>
      )}
    </div>
  );
}

// ── Inline Styles (for simple/dynamic properties) ──

const inlineStyles: Record<string, React.CSSProperties> = {
  content: {
    padding: '1em 1.25em 1.25em',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75em',
  },

  // Padding for non-first columns (spacing from the absolute divider line)
  colPadding: {
    paddingLeft: '0.5em',
  },

  // Product info grid — each row auto-aligns across all product columns
  // rowGap: 0 so grid cells are flush (absolute divider draws the continuous line)
  infoGrid: {
    display: 'grid',
    gap: '0 0.75em',
  },

  category: {
    fontSize: '0.7em',
    fontWeight: 500,
    color: 'var(--color-gray-400, #a8a5a0)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  productName: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: '0.85em',
    fontWeight: 500,
    lineHeight: 1.3,
    color: 'var(--color-primary, #1a1a1a)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.375em',
  },

  price: {
    fontSize: '0.9em',
    fontWeight: 600,
    color: 'var(--color-primary, #1a1a1a)',
  },

  priceStrike: {
    fontSize: '0.75em',
    color: 'var(--color-gray-400, #a8a5a0)',
    textDecoration: 'line-through',
  },

  badgeRow: {},

  subtitle: {
    fontSize: '0.72em',
    color: 'var(--color-gray-500, #7a7874)',
    lineHeight: 1.3,
  },

  // Recommendation
  recommendation: {
    background: 'var(--color-sand, #f5f3f0)',
    borderLeft: '3px solid var(--color-accent, #c45d3a)',
    borderRadius: '0 8px 8px 0',
    padding: '0.625em 0.75em',
  },

  recommendationText: {
    fontSize: '0.85em',
    fontStyle: 'italic',
    lineHeight: 1.5,
    color: 'var(--color-gray-600, #5c5a57)',
  },

  // Differences
  diffsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125em',
  },

  sectionLabel: {
    fontSize: '0.7em',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--color-gray-400, #a8a5a0)',
    marginBottom: '0.25em',
  },

  diffAttrLabel: {
    fontSize: '0.7em',
    fontWeight: 500,
    color: 'var(--color-gray-500, #7a7874)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '0.125em',
  },

  diffValuesRow: {
    display: 'flex',
    gap: '0.75em',
  },

  diffValue: {
    flex: 1,
    fontSize: '0.8em',
    fontWeight: 500,
    color: 'var(--color-primary, #1a1a1a)',
    lineHeight: 1.4,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Shared traits
  sharedSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375em',
    alignItems: 'center',
  },

  sharedRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375em',
    justifyContent: 'center',
  },

  sharedChip: {
    fontSize: '0.72em',
    color: 'var(--color-gray-600, #5c5a57)',
    background: 'var(--color-sand, #f5f3f0)',
    borderRadius: '9999px',
    padding: '0.25em 0.625em',
  },

  expandBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.75em',
    color: 'var(--color-gray-500, #7a7874)',
    padding: '0.25em 0',
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'color 0.15s ease',
  },

  // CTAs
  ctaRow: {
    display: 'flex',
    gap: '0.75em',
  },

  // Dots
  dotsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.375em',
    padding: '0.5em 0',
    background: 'var(--color-sand, #f5f3f0)',
  },

  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '9999px',
    border: 'none',
    background: 'var(--color-gray-300, #d1cfc9)',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.2s ease',
  },

  dotActive: {
    width: '18px',
    borderRadius: '4px',
    background: 'var(--color-primary, #1a1a1a)',
  },

  // Skeleton
  skeletonImage: {
    flex: 1,
    height: '100%',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.3)',
  },

  skeletonLine: {
    height: '0.85em',
    borderRadius: '4px',
    background: 'var(--color-stone, #e8e6e3)',
  },
};

export default ComparisonView;
